/* import __COLOCATED_TEMPLATE__ from './copilot-composer.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type InboxState from 'embercom/services/inbox-state';
import type InboxHotkeys from 'embercom/services/inbox-hotkeys';
export interface CopilotComposerArgs {
  inputValue: string;
  updateText: (event: InputEvent & { target: HTMLTextAreaElement }) => void;
  sendMessage: () => Promise<void>;
  isDisabled: boolean;
  tooltipContent: string | undefined;
  conversationLength?: number;
  isCopilotVisible: boolean;
}

interface Signature {
  Element: Element;
  Args: CopilotComposerArgs;
  Blocks: {
    filters: [];
    default: [];
  };
}

export default class CopilotComposer extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare inboxState: InboxState;
  @service declare inboxHotkeys: InboxHotkeys;

  @tracked isComposerFocused = false;
  @tracked isComposing = false;
  @tracked isEnterDisabled = false;

  autoResizeModifier = modifier(
    (
      element: HTMLTextAreaElement,
      _positional,
      {
        inputValue,
      }: {
        inputValue?: string;
      },
    ) => {
      element.style.height = 'auto';
      if (this.args.isCopilotVisible) {
        element.style.height = `${element.scrollHeight}px`;
      }
      return () => {
        // we don't do anything with this inputValue, we want this modifier to
        //  recompute based on the source of truth input value, so we no-op
        // evaluate it here. the same as `let a = 1; a;`
        inputValue;
      };
    },
  );

  get isTooltipEnabled() {
    return this.args.tooltipContent !== undefined;
  }

  get placeholderText() {
    if (this.args.conversationLength && this.args.conversationLength > 0) {
      return this.intl.t('inbox.ask-fin.ask-a-follow-up-question');
    } else {
      return this.intl.t('inbox.ask-fin.ask-a-question');
    }
  }

  get shouldBlockEnter(): boolean {
    return this.isComposing || this.isEnterDisabled;
  }

  @action
  handleEnterKey(event: KeyboardEvent) {
    if (this.shouldBlockEnter) {
      return false;
    }

    if (event.shiftKey) {
      //Prevents shift+enter from sending a message
      event.preventDefault();
      return;
    }

    return this.args.sendMessage();
  }

  @action onFocus() {
    this.isComposerFocused = true;
  }

  @action onBlur() {
    this.isComposerFocused = false;
  }

  @action handleSendHotkey() {
    if (this.isComposerFocused) {
      this.args.sendMessage();
    }
  }

  @action
  handleCompositionStart() {
    this.isComposing = true;
  }

  @action
  handleCompositionEnd() {
    this.isComposing = false;

    // Safari/Chrome workaround
    this.isEnterDisabled = true;
    setTimeout(() => {
      this.isEnterDisabled = false;
    }, 300);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Copilot::Composer::CopilotComposer': typeof CopilotComposer;
    'inbox2/copilot/composer/copilot-composer': typeof CopilotComposer;
  }
}
